import React, { useState, useEffect } from 'react';
import { DraggableModal } from 'src/app/components';
import 'src/app/popup.scss';
import styles from 'src/app/pages/Administration/tabs/OntologyTab/Ontology.module.scss';

import { OntologyDataType } from 'src/types/services/ontology-service.type';
import { ontologyService } from 'src/app/services';

import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { contentsActions } from 'src/app/redux/actions';

import { errorLocals } from 'src/locals';
import { statusHelper } from 'src/app/helpers';

import { OntologyEnum } from 'src/enums/ontology.enum';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { OntologyFormType } from './ontologiesForm.config';
import { displaySuccessStatusMessage } from 'src/app/helpers/status.helper';
import { useTranslation } from 'react-i18next';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Select from 'antd/es/select';

const OntologyForm: React.FC<OntologyFormType> = ({
  setOnClickOpen,
  addOntologyType,
  onClickOpen,
  ontologiesForm,
  setEditModal,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const defaultOntologyData = {
    id: '',
    ontologyType: addOntologyType ? addOntologyType : undefined,
    email: undefined,
    phrase: '',
    abbreviation: '',
    definition: '',
  };

  const { TextArea } = Input;

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const [ontologyDataInForm, setOntologyDataInForm] = useState<OntologyDataType>(ontologiesForm || defaultOntologyData);
  const [selectedOntology, setSelectedOntology] = useState<string | null>(null);

  const [form] = Form.useForm();

  useEffect(() => {
    if (addOntologyType) {
      setOntologyDataInForm({
        ...ontologyDataInForm,
        ontologyType: addOntologyType,
      });
    }
  }, []);

  useEffect(() => {
    if (
      ontologiesForm?.ontologyType === OntologyEnum.Player_Audience_Entity ||
      addOntologyType === OntologyEnum.Player_Audience_Entity
    ) {
      setSelectedOntology('Player_Audience_Entity');
    } else {
      setSelectedOntology(null);
    }
  }, [setEditModal, addOntologyType]);

  const onFormChange = (inputLabel: keyof OntologyDataType, value: any) => {
    setOntologyDataInForm({
      ...ontologyDataInForm,
      [inputLabel]: value,
    });

    if (inputLabel === 'ontologyType' && value === OntologyEnum.Player_Audience_Entity) {
      setSelectedOntology('Player_Audience_Entity');
    } else if (inputLabel === 'ontologyType' && value !== OntologyEnum.Player_Audience_Entity) {
      setSelectedOntology(null);
    }
  };

  const onFormSave = async () => {
    if (setEditModal) {
      try {
        const ontologyEditData = await ontologyService.ontologyEdit(ontologyDataInForm);

        setOntologyDataInForm(defaultOntologyData);
        dispatch(contentsActions.ontologyEdited(ontologyEditData.data));
        displaySuccessStatusMessage(t('success.edited'));
        setOnClickOpen(false);
      } catch (error: any) {
        if (error !== errorLocals.errorHandledError) {
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    } else {
      try {
        const ontologyAddData = await ontologyService.createOntologyOfEvent(ontologyDataInForm, {
          blockErrorHandler: true,
        });

        setOntologyDataInForm(defaultOntologyData);
        dispatch(contentsActions.ontologyAdded(ontologyAddData.data));
        displaySuccessStatusMessage(t('success.added'));
        setOnClickOpen(false);
      } catch (error) {
        if (error !== errorLocals.errorHandledError) {
          // @ts-ignore
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    }
  };

  return (
    <>
      {onClickOpen && (
        <DraggableModal
          headerText={
            setEditModal ? t('administration.ontologies.editOntology') : t('administration.ontologies.addOntology')
          }
          onClickClose={() => setOnClickOpen(false)}
          style={{ width: '700px', height: '550px' }}
        >
          <Form
            initialValues={ontologyDataInForm}
            scrollToFirstError={{ behavior: 'smooth' }}
            onFinish={onFormSave}
            form={form}
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className="popup-content">
                  <div className={styles.container} style={{ padding: 10, marginRight: 10 }}>
                    <div className="popup-row">
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('administration.ontologies.selectOntology')}</span>
                        <Form.Item
                          name="ontologyType"
                          rules={[{ required: true, message: t('general.required') || '' }]}
                        >
                          <Select
                            showSearch
                            className={styles.select}
                            placeholder={t('administration.ontologies.selectAnOntology')}
                            optionFilterProp="children"
                            defaultValue={selectedOntology || undefined}
                            onChange={(value) => onFormChange('ontologyType', value)}
                            dropdownClassName="selectOntology"
                            disabled={addOntologyType ? true : false}
                          >
                            {getEnumSelectOptions(OntologyEnum).map((o) => (
                              <Select.Option key={o[1]} value={o[1]}>
                                {t(`administration.ontologies.ontologyEnum.${o[0]}`)}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </div>

                    {selectedOntology === 'Player_Audience_Entity' && (
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('administration.ontologies.email')}</span>
                        <Form.Item name="email">
                          <Input
                            className={styles.standard_input}
                            type="email"
                            value={ontologyDataInForm.email || undefined}
                            onChange={(e) => onFormChange('email', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    )}

                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.ontologies.phrase')}</span>
                      <Form.Item name="phrase" rules={[{ required: true, message: t('general.required') || '' }]}>
                        <Input
                          className={styles.standard_input}
                          value={ontologyDataInForm.phrase}
                          onChange={(e) => onFormChange('phrase', e.target.value)}
                        />
                      </Form.Item>
                    </div>

                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.ontologies.abbreviation')}</span>
                      <Form.Item
                        name="abbreviation"
                        rules={[
                          { required: true, message: t('general.required') || '' },
                          () => ({
                            validator(_, value) {
                              if (!value) {
                                return Promise.resolve();
                              }

                              if (userState.userName !== 'Admin' && value.trim().toLowerCase() === 'admin') {
                                return Promise.reject(t('administration.ontologies.thisAbbCannotBeAdded'));
                              }

                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input
                          className={styles.standard_input}
                          value={ontologyDataInForm.abbreviation}
                          onChange={(e) => onFormChange('abbreviation', e.target.value)}
                        />
                      </Form.Item>
                    </div>

                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('administration.ontologies.definition')}</span>
                      <Form.Item name="definition" rules={[{ required: true, message: t('general.required') || '' }]}>
                        <TextArea
                          rows={3}
                          showCount
                          maxLength={500}
                          value={ontologyDataInForm.definition}
                          onChange={(e) => onFormChange('definition', e.target.value)}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="popup-footer" style={{ marginRight: '5%' }}>
                    <div>
                      <button className="next-button" type="submit">
                        {t('general.save')}
                      </button>
                      <button className="modal-button" onClick={() => setOnClickOpen(false)} type="button">
                        {t('general.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}
    </>
  );
};

export default OntologyForm;
